import React from "react";
import {
  AbsoluteFill,
  Audio,
  type CalculateMetadataFunction,
  interpolate,
  Sequence,
  staticFile,
} from "remotion";
import { z } from "zod";
import { SfxContext, Sfx } from "../b2b/ui/Sfx";
import { COPY, MerchantCtx } from "./copy";
import { getMerchant, MERCHANT_SLUGS } from "./merchants";
import { End } from "./scenes/End";
import { Hook } from "./scenes/Hook";
import { Offer } from "./scenes/Offer";
import { Payoff } from "./scenes/Payoff";
import { Showcase } from "./scenes/Showcase";
import { StageFrame } from "./stage";
import {
  MERCHANT_FPS,
  type SceneKey,
  type SceneProps,
  timelineFor,
} from "./timing";

if (MERCHANT_SLUGS.length === 0) {
  throw new Error(
    "No renderable merchant in the snapshot (logo + photo + reward). Run `npm run sync:brands`.",
  );
}

export const merchantSchema = z.object({
  /** Brand slug, from the live snapshot (`npm run sync:brands`). */
  slug: z.enum(MERCHANT_SLUGS as [string, ...string[]]),
  lang: z.enum(["fr", "en"]),
  cut: z.enum(["spot", "bumper"]),
  /**
   * Music bed. Off for organic TikTok/Reels posts, where a trending sound
   * is attached in-app (the platforms reward native audio); on for ads,
   * the landing, and everything else.
   */
  music: z.boolean(),
  /** Sound effects (bubbles, pops, coins). */
  sfx: z.boolean(),
});

export type MerchantVideoProps = z.infer<typeof merchantSchema>;

const SCENES: Record<SceneKey, React.FC<SceneProps>> = {
  hook: Hook,
  showcase: Showcase,
  offer: Offer,
  payoff: Payoff,
  end: End,
};

/**
 * Per-merchant B2C commercial. One template, rendered for every live brand
 * from its snapshot + brief (see `merchants.ts`, `briefs.ts`):
 *
 *   hook      the brand's best photo, "what is it??" → "{name}!" + link
 *   showcase  logo + tagline + photos dealt on the beat
 *   offer     brand colour: recommend {name} → up to {reward} per order,
 *             cashback for the friend, the disclaimer
 *   payoff    a sale through your link lands → real cash, not points
 *   end       logo × Frak, get your link on {site} or in the app
 *
 * Brand first, Frak as the mechanism: it has to be something the merchant
 * is happy to post on its own Instagram. Every cut is on the beat of
 * `song.mp3` (see `timing.ts`).
 */
export const MerchantVideo: React.FC<MerchantVideoProps> = ({
  slug,
  lang,
  cut,
  music,
  sfx,
}) => {
  const merchant = getMerchant(slug);
  const { scenes, duration } = timelineFor(cut);
  return (
    <MerchantCtx.Provider value={{ merchant, lang, copy: COPY[lang] }}>
      <SfxContext.Provider value={sfx}>
        <AbsoluteFill style={{ background: "#000000" }}>
          {music ? (
            <Audio
              src={staticFile("song.mp3")}
              volume={(f) =>
                interpolate(f, [0, 4, duration - 18, duration], [0, 1, 1, 0], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                }) * 0.3
              }
            />
          ) : null}
          <StageFrame>
            {scenes.map(({ key, from, frames }, i) => {
              const Scene = SCENES[key];
              return (
                <Sequence
                  key={key}
                  name={key}
                  from={from}
                  durationInFrames={frames}
                  premountFor={MERCHANT_FPS}
                >
                  <Scene first={i === 0} />
                </Sequence>
              );
            })}
          </StageFrame>
          {/* Cut whooshes land on the cut (they start a little before it). */}
          {scenes.slice(1).map(({ key, from }) => (
            <Sfx key={key} sound="swoosh" at={from} volume={0.8} />
          ))}
        </AbsoluteFill>
      </SfxContext.Provider>
    </MerchantCtx.Provider>
  );
};

export const calculateMerchantMetadata: CalculateMetadataFunction<
  MerchantVideoProps
> = ({ props }) => ({
  durationInFrames: timelineFor(props.cut).duration,
  defaultOutName: `merchant-${props.slug}-${props.cut}-${props.lang}`,
});
